import { CircularProgress, Skeleton, ThemeProvider } from "@mui/material";
import { useState } from "react";
import { ReactComponent as FileMessageIcon } from "../../../assets/Chat/file-message.svg";
import strings from "../../../strings";
import { getTranslation } from "../../../utils/getTranslation";
import { downloadFile } from "../../../utils/messageChat";
import { theme } from "../../../utils/theme";
import AudioPlayer from "../../AudioPlayer/AudioPlayer";
import ModalWindow from "../../ModalWindow/ModalWindow";
import s from "./TextChatMessage.module.css";
import {
  ResolvedTextChatMessageFile,
  TextChatBodyRenderer,
  TextChatBodyRendererProps,
} from "./TextChatMessage.types";

const imageTypes = ["jpeg", "png", "jpg", "gif", "avif", "webp"];

const getFileExtension = (filename: string, type: string) => {
  if (type === "plain") {
    return "txt";
  }

  return filename.split(".").pop() || type;
};

const normalizeInlineFile = (
  file: unknown,
  index: number,
): ResolvedTextChatMessageFile | null => {
  if (typeof file === "string") {
    return {
      id: `${index}`,
      type: "jpg",
      source: file,
      filename: `file-${index + 1}`,
    };
  }

  if (!file || typeof file !== "object") {
    return null;
  }

  const value = file as {
    id?: string;
    _id?: string;
    type?: string;
    source?: string;
    image?: string;
    link?: string;
    filename?: string;
    name?: string;
    size?: string | number;
  };
  const source = value.source || value.image || value.link;

  if (!source) {
    return null;
  }

  return {
    id: value.id || value._id || `${index}`,
    type: value.type || "file",
    source,
    filename: value.filename || value.name || `file-${index + 1}`,
    size:
      typeof value.size === "string"
        ? value.size
        : value.size
          ? `${value.size}`
          : undefined,
  };
};

const getFilesForRender = ({
  message,
  files,
}: TextChatBodyRendererProps): ResolvedTextChatMessageFile[] =>
  files?.length
    ? files
    : ((message.files || [])
        .map((file, index) => normalizeInlineFile(file, index))
        .filter(Boolean) as ResolvedTextChatMessageFile[]);

const getReplyPreviewSummary = (
  replyToMessage: TextChatBodyRendererProps["message"]["replyToMessage"],
) => {
  if (!replyToMessage) {
    return "";
  }

  if (replyToMessage.message?.trim()) {
    return replyToMessage.message;
  }

  if (replyToMessage.files?.length) {
    return strings.globalChatAttachmentPreview;
  }

  return strings.globalChatVoiceMessagePreview;
};

const getReplyPreviewSenderName = (
  replyToMessage: TextChatBodyRendererProps["message"]["replyToMessage"],
) => {
  if (!replyToMessage) {
    return "";
  }

  if (replyToMessage.user === "System") {
    return strings.globalChatSystemSender;
  }

  return `${getTranslation(replyToMessage.user.name) ?? ""} ${
    getTranslation(replyToMessage.user.surname) ?? ""
  }`.trim();
};

const ReplyPreview = ({
  replyToMessage,
  onNavigateToMessage,
}: {
  replyToMessage: TextChatBodyRendererProps["message"]["replyToMessage"];
  onNavigateToMessage?: TextChatBodyRendererProps["onNavigateToMessage"];
}) => {
  if (!replyToMessage) {
    return null;
  }

  const content = (
    <>
      <span className={s.replyPreviewLabel}>
        {getReplyPreviewSenderName(replyToMessage) || strings.globalChatReply}
      </span>
      <span className={s.replyPreviewText}>
        {getReplyPreviewSummary(replyToMessage)}
      </span>
    </>
  );

  if (onNavigateToMessage) {
    return (
      <button
        type="button"
        className={`${s.replyPreview} ${s.replyPreviewInteractive}`}
        data-prevent-reaction-picker="true"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onNavigateToMessage(replyToMessage._id);
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={s.replyPreview} data-prevent-reaction-picker="true">
      {content}
    </div>
  );
};

export const TextMessageBody: TextChatBodyRenderer = (props) => {
  const {
    message,
    isUserMessage,
    name,
    surname,
    showName,
    onMediaInteraction,
    onNavigateToMessage,
  } = props;
  const [imageModalIsOpen, setImageModalIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const files = getFilesForRender(props);
  const readyFiles = files.filter(
    (file) => (file.status || "ready") === "ready",
  );
  const failedFiles = files.filter((file) => file.status === "failed");
  const hasUnresolvedFiles = Boolean(message.files?.length) && !files.length;

  const openImage = (source: string) => {
    onMediaInteraction?.();
    setSelectedImage(source);
    setImageModalIsOpen(true);
  };

  return (
    <div
      className={s.messageContainer}
      style={{ marginBottom: message.userId === "System" ? 0 : "10px" }}
    >
      {showName && name && message.senderKind !== "currentUser" && (
        <div className={s.userName}>{name + " " + (surname || "")}</div>
      )}
      <ReplyPreview
        replyToMessage={message.replyToMessage}
        onNavigateToMessage={onNavigateToMessage}
      />
      {hasUnresolvedFiles && (
        <div className={s.skeletonFilesBlock} aria-label="Loading files">
          {message.files?.map((_, index) => (
            <Skeleton variant="rounded" width={100} height={100} key={index} />
          ))}
        </div>
      )}
      {failedFiles.length > 0 && (
        <div className={s.failedFilesBlock}>
          {failedFiles.map((file) => (
            <div className={s.failedFile} key={file.id || file.filename}>
              <span>
                {strings.failedToDownloadFile || strings.failedStatus}
              </span>
              {file.onRetry && (
                <button
                  type="button"
                  data-prevent-reaction-picker="true"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    file.onRetry?.();
                  }}
                >
                  {strings.retry}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {readyFiles.length > 0 && (
        <div className={s.filesSection}>
          {readyFiles.map((file) =>
            imageTypes.includes(file.type) ? (
              <img
                src={file.source}
                alt=""
                key={file.id || file.source}
                className={s.file}
                data-prevent-reaction-picker="true"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openImage(file.source);
                }}
              />
            ) : (
              <button
                type="button"
                key={file.id || file.source}
                className={s.fileBlock}
                data-prevent-reaction-picker="true"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onMediaInteraction?.();
                  downloadFile(
                    file.source,
                    file.filename.split(".")[0],
                    getFileExtension(file.filename, file.type),
                  );
                }}
              >
                <FileMessageIcon
                  className={isUserMessage ? s.fileMessage : s.fileMessageOther}
                />
                <div
                  className={
                    isUserMessage ? s.fileInfoBlock : s.fileInfoBlockOther
                  }
                >
                  <span>{file.filename}</span>
                  {file.size && <span>{file.size}</span>}
                </div>
              </button>
            ),
          )}
        </div>
      )}
      {message.attachments.length > 0 &&
        readyFiles.length === 0 &&
        !failedFiles.length && (
          <div className={s.loaderBlock}>
            <ThemeProvider theme={theme}>
              <CircularProgress size={100} color="secondary" />
            </ThemeProvider>
          </div>
        )}
      {message.text && <p className={s.messageText}>{message.text}</p>}
      <ModalWindow
        isOpen={imageModalIsOpen}
        setIsOpen={setImageModalIsOpen}
        height="fit-content"
        width="fit-content"
        bgColor="transparent"
        padding="5px"
      >
        <div
          className={s.modalImageBlock}
          onContextMenu={(event) => {
            event.stopPropagation();
          }}
        >
          <img src={selectedImage} alt="" className={s.modalImage} />
        </div>
      </ModalWindow>
    </div>
  );
};

export const SystemMessageBody: TextChatBodyRenderer = ({ message }) => (
  <div className={s.messageContainer} style={{ marginBottom: 0 }}>
    <p className={s.messageText}>{message.text}</p>
  </div>
);

export const VoiceMessageBody: TextChatBodyRenderer = ({
  message,
  onMediaInteraction,
  onNavigateToMessage,
}) => (
  <div className={s.messageContainer}>
    <ReplyPreview
      replyToMessage={message.replyToMessage}
      onNavigateToMessage={onNavigateToMessage}
    />
    {message.voice?.url ? (
      <div className={s.voicePlayer} data-prevent-reaction-picker="true">
        <AudioPlayer
          url={message.voice.url}
          filepath={message.voice.filepath}
          downloadIsAvailable={false}
          onError={onMediaInteraction}
        />
      </div>
    ) : (
      <div className={s.failedFile}>{strings.failedToDownloadFile}</div>
    )}
  </div>
);

export const BookingMessageBody: TextChatBodyRenderer = ({
  message,
  name,
  surname,
}) => {
  const booking = message.realTimeBooking;

  return (
    <div className={s.messageContainer}>
      <p className={s.messageText}>
        {message.senderKind === "otherUser"
          ? `${strings.receivedRequesrSession} ${name || ""} ${surname || ""}`
          : strings.sessionRequest}
      </p>
      {booking?.subject && <p className={s.messageText}>{booking.subject}</p>}
      {booking?.isAccepted && (
        <p className={s.messageText}>{strings.specialistConfirmedRequest}</p>
      )}
      {booking?.isExpired && (
        <p className={s.messageText}>{strings.canceledSession}</p>
      )}
    </div>
  );
};

export const DEFAULT_TEXT_CHAT_BODY_RENDERERS = {
  text: TextMessageBody,
  system: SystemMessageBody,
  booking: BookingMessageBody,
  deleted: SystemMessageBody,
  edited: TextMessageBody,
  offer: TextMessageBody,
  payment: TextMessageBody,
  moderation: SystemMessageBody,
  voice: VoiceMessageBody,
};
